import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Plus,
  Trash2,
  Crown,
  Loader2,
  ArrowLeft,
  Layers,
  Wallet,
  TrendingDown,
  ShieldCheck,
  Target,
  Pencil,
  Check,
  ListChecks,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "../components/layout/DashboardLayout";
import { api, formatApiError, PFA_REQUEST_QUEUED } from "../lib/api";
import { SCHEMAS, numOr, todayISO } from "../components/bulk/bulkSchemas";
import { formatINR } from "../lib/currency";
import MoneyInput from "@/components/ui/MoneyInput";
import DateField from "@/components/ui/DateField";
import { Checkbox } from "@/components/ui/checkbox";

// Per-entity theming + list route to return to.
const ENTITY_META = {
  assets: {
    accent: "emerald",
    icon: Wallet,
    back: "/dashboard/assets",
    noun: "asset",
    primaryKey: "value",
    primaryLabel: "Current Value",
  },
  liabilities: {
    accent: "rose",
    icon: TrendingDown,
    back: "/dashboard/liabilities",
    noun: "liability",
    primaryKey: "outstanding",
    primaryLabel: "Outstanding",
  },
  insurance: {
    accent: "sky",
    icon: ShieldCheck,
    back: "/dashboard/insurance",
    noun: "policy",
    primaryKey: "sum_assured",
    primaryLabel: "Sum Assured",
  },
  goals: {
    accent: "violet",
    icon: Target,
    back: "/dashboard/goals",
    noun: "goal",
    primaryKey: "target_amount",
    primaryLabel: "Target Amount",
  },
};

const TEXT_ACCENT = {
  emerald: "text-emerald-600",
  rose: "text-rose-600",
  sky: "text-sky-600",
  violet: "text-violet-600",
};

const GRAD = {
  emerald: "from-emerald-500/20 via-emerald-500/5 to-transparent",
  rose: "from-rose-500/20 via-rose-500/5 to-transparent",
  sky: "from-sky-500/20 via-sky-500/5 to-transparent",
  violet: "from-violet-500/20 via-violet-500/5 to-transparent",
};
const RING = {
  emerald: "ring-emerald-500/40",
  rose: "ring-rose-500/40",
  sky: "ring-sky-500/40",
  violet: "ring-violet-500/40",
};
const ICON_BG = {
  emerald: "bg-emerald-500/15 text-emerald-600",
  rose: "bg-rose-500/15 text-rose-600",
  sky: "bg-sky-500/15 text-sky-600",
  violet: "bg-violet-500/15 text-violet-600",
};

// ── Field renderer (label + input) ──
function Field({ col, row, type, onChange }) {
  const label = typeof col.label === "function" ? col.label(type) : col.label;
  const ph = col.placeholder ? col.placeholder(type) : "";
  const val = row[col.key] ?? "";
  const base =
    "w-full text-sm px-3 py-2 rounded-xl border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition";

  if (col.kind === "switch") {
    return (
      <label className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2 bg-white dark:bg-slate-900 cursor-pointer">
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {label}
        </span>
        <Checkbox
          checked={!!row[col.key]}
          onCheckedChange={(checked) => onChange(col.key, checked === true)}
        />
      </label>
    );
  }

  if (col.kind === "money") {
    return (
      <div className="space-y-1">
        <label className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
          {label}
          {col.required && <span className="text-rose-500">*</span>}
        </label>

        <MoneyInput
          value={val}
          onValueChange={(raw) => onChange(col.key, raw)}
          placeholder={ph || label}
        />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
        {label}
        {col.required && <span className="text-rose-500"> *</span>}
      </label>
      {col.kind === "select" ? (
        <select
          value={val}
          onChange={(e) => onChange(col.key, e.target.value)}
          className={base}
        >
          {col.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : col.kind === "date" ? (
        <DateField
          value={val}
          max={col.key.includes("purchase") ? todayISO() : undefined}
          onChange={(v) => onChange(col.key, v)}
        />
      ) : (
        <div className="relative">
          <input
            type={
              col.kind === "number" || col.kind === "percent"
                ? "number"
                : "text"
            }
            value={val}
            placeholder={ph || label}
            onChange={(e) => onChange(col.key, e.target.value)}
            className={`${base}  ${col.kind === "percent" ? "pr-8" : ""}`}
          />
          {col.kind === "percent" && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
              %
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Human-readable value for a single column in the preview.
function displayValue(col, row, type) {
  const raw = row[col.key];
  if (col.kind === "switch") return raw ? "Yes" : "No";
  if (raw === "" || raw == null) return null;
  if (col.kind === "money") return formatINR(raw);
  if (col.kind === "percent") return `${raw}%`;
  if (col.kind === "select") {
    const opt = col.options?.find((o) => String(o.value) === String(raw));
    return opt ? opt.label : raw;
  }
  return String(raw);
}

// Read-only summary card for one entry in the preview step.
function PreviewCard({ row, index, schema, meta, onEdit }) {
  const type = row[schema.typeKey];
  const typeLabel =
    schema.typeOptions.find((o) => o.value === type)?.label || type;
  const cols = schema.columns.filter((c) => c.active(type));
  const primaryCol = cols.find((c) => c.key === meta.primaryKey);
  const primaryVal = primaryCol ? displayValue(primaryCol, row, type) : null;

  // Everything except name + the primary metric goes into the detail grid.
  const detailCols = cols.filter(
    (c) => c.key !== "name" && c.key !== meta.primaryKey,
  );

  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span
            className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg text-xs font-bold ${ICON_BG[meta.accent]}`}
          >
            {index + 1}
          </span>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
              {typeLabel}
            </div>
            <div className="font-display text-lg font-bold text-slate-900 dark:text-white truncate">
              {row.name?.trim() || "Untitled"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {primaryVal && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">
                {meta.primaryLabel}
              </div>
              <div
                className={`font-bold text-base ${TEXT_ACCENT[meta.accent]}`}
              >
                {primaryVal}
              </div>
            </div>
          )}
          <button
            onClick={() => onEdit(index)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-emerald-400 hover:text-emerald-600 transition"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-3">
        {detailCols.map((c) => {
          const val = displayValue(c, row, type);
          if (val == null) return null;
          const label =
            typeof c.label === "function" ? c.label(type) : c.label;
          return (
            <div key={c.key} className="min-w-0">
              <div className="text-[11px] text-slate-400 truncate">{label}</div>
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                {val}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Section header above a type's entries in the preview.
function GroupLabel({ meta, label, count }) {
  return (
    <div className="flex items-center gap-2 px-1">
      <span
        className={`grid h-6 w-6 place-items-center rounded-lg ${ICON_BG[meta.accent]}`}
      >
        <meta.icon className="h-3.5 w-3.5" />
      </span>
      <span className="text-sm font-bold text-slate-900 dark:text-white">
        {label}
      </span>
      <span className="text-xs text-slate-400">
        · {count} {count === 1 ? "entry" : "entries"}
      </span>
    </div>
  );
}

export default function BulkAdd() {
  const { entity } = useParams();
  const navigate = useNavigate();
  const schema = SCHEMAS[entity];
  const meta = ENTITY_META[entity];

  const [usage, setUsage] = useState(null); // { used, limit(null=unlimited) }
  // Grouped model: each group has ONE type + many entries of that type.
  // groups = [{ id, type, entries: [{ [typeKey]: type, ...fields }] }]
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [mode, setMode] = useState("edit"); // "edit" | "preview"
  const [edit, setEdit] = useState(null); // single-entry edit in preview: { gi, ei }
  const gid = useRef(0);

  const makeEntry = (type = schema?.defaultType) => ({
    [schema.typeKey]: type,
    ...schema.defaultsFor(type),
  });
  const makeGroup = (type = schema?.defaultType) => ({
    id: ++gid.current,
    type,
    entries: [makeEntry(type)],
  });

  useEffect(() => {
    if (!schema) return;
    api
      .get("/subscription/usage")
      .then((r) => {
        const u = r.data?.usage?.[schema.usageKey] || {};
        setUsage({
          used: u.used || 0,
          limit: r.data?.is_premium ? null : (u.limit ?? null),
        });
      })
      .catch(() => setUsage({ used: 0, limit: null }))
      .finally(() => setGroups([makeGroup()]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  const maxNew = useMemo(() => {
    if (!usage || usage.limit == null) return Infinity;
    return Math.max(0, usage.limit - usage.used);
  }, [usage]);

  const flatCount = useMemo(
    () => groups.reduce((s, g) => s + g.entries.length, 0),
    [groups],
  );
  const atCap = flatCount >= maxNew;

  if (!schema || !meta) {
    return (
      <DashboardLayout>
        <div className="card-soft p-12 text-center text-slate-500">
          Unknown bulk type.
        </div>
      </DashboardLayout>
    );
  }

  const typeLabel = (type) =>
    schema.typeOptions.find((o) => o.value === type)?.label || type;

  // ── Group / entry mutators ──
  const setGroupType = (gi, type) =>
    setGroups((gs) =>
      gs.map((g, i) =>
        i === gi
          ? {
              ...g,
              type,
              // Re-seed each entry with the new type's defaults, keep the name.
              entries: g.entries.map((e) => ({
                ...makeEntry(type),
                name: e.name || "",
              })),
            }
          : g,
      ),
    );
  const setEntryCell = (gi, ei, key, v) =>
    setGroups((gs) =>
      gs.map((g, i) =>
        i === gi
          ? {
              ...g,
              entries: g.entries.map((e, j) =>
                j === ei ? { ...e, [key]: v } : e,
              ),
            }
          : g,
      ),
    );
  const addEntry = (gi) =>
    !atCap &&
    setGroups((gs) =>
      gs.map((g, i) =>
        i === gi ? { ...g, entries: [...g.entries, makeEntry(g.type)] } : g,
      ),
    );
  const removeEntry = (gi, ei) =>
    setGroups((gs) => {
      const next = gs.map((g, i) =>
        i === gi ? { ...g, entries: g.entries.filter((_, j) => j !== ei) } : g,
      );
      // Drop a group that ran out of entries (but always keep ≥1 group).
      const pruned = next.filter((g) => g.entries.length > 0);
      return pruned.length ? pruned : [makeGroup()];
    });
  const addGroup = () => !atCap && setGroups((gs) => [...gs, makeGroup()]);
  const removeGroup = (gi) =>
    setGroups((gs) => {
      const next = gs.filter((_, i) => i !== gi);
      return next.length ? next : [makeGroup()];
    });

  // Validate every entry across all groups (toasts the first error).
  const validateAll = () => {
    for (let gi = 0; gi < groups.length; gi++) {
      const g = groups[gi];
      for (let ei = 0; ei < g.entries.length; ei++) {
        const err = schema.validate(g.entries[ei]);
        if (err) {
          toast.error(`${typeLabel(g.type)} #${ei + 1}: ${err}`);
          return false;
        }
      }
    }
    if (usage?.limit != null && usage.used + flatCount > usage.limit) {
      toast.error(
        `Free plan allows ${usage.limit}. You have ${usage.used}; remove some or upgrade.`,
      );
      return false;
    }
    return true;
  };

  const goPreview = () => {
    if (!validateAll()) return;
    setEdit(null);
    setMode("preview");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToEditAll = () => {
    setEdit(null);
    setMode("edit");
  };

  const submit = async () => {
    if (!validateAll()) return;
    setSaving(true);
    setProgress({ done: 0, total: flatCount });
    try {
      // Flatten all groups' entries into one atomic bulk insert.
      const items = groups.flatMap((g) => g.entries.map((e) => schema.build(e)));
      const res = await api.post(`${schema.endpoint}/bulk`, items);
      const n = res?.data?.count ?? items.length;
      // An advisor acting-as-client has this queued for approval, not applied.
      if (res?.config?.[PFA_REQUEST_QUEUED]) {
        toast.success(
          `Submitted ${n} ${n === 1 ? meta.noun : entity} for your client's approval`,
        );
      } else {
        toast.success(`Added ${n} ${n === 1 ? meta.noun : entity}`);
      }
      navigate(meta.back);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProgress(null);
      setSaving(false);
    }
  };

  const Icon = meta.icon;

  // Editable entry card (fields only — the TYPE lives on the group header).
  const editableEntry = (group, gi, entry, ei, { inline = false } = {}) => {
    const cols = schema.columns.filter((c) => c.active(group.type));
    return (
      <div
        className={`rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 ${
          inline ? `ring-2 ${RING[meta.accent]}` : ""
        }`}
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <span
            className={`grid h-6 w-6 place-items-center rounded-lg text-[11px] font-bold ${ICON_BG[meta.accent]}`}
          >
            {ei + 1}
          </span>
          {inline ? (
            <button
              onClick={() => {
                const err = schema.validate(entry);
                if (err)
                  return toast.error(
                    `${typeLabel(group.type)} #${ei + 1}: ${err}`,
                  );
                setEdit(null);
              }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition"
            >
              <Check className="h-3.5 w-3.5" /> Done
            </button>
          ) : (
            <button
              onClick={() => removeEntry(gi, ei)}
              disabled={flatCount === 1}
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-30"
              title="Remove entry"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {cols.map((c) => (
            <div
              key={c.key}
              className={c.kind === "switch" ? "flex items-end" : ""}
            >
              <Field
                col={c}
                row={entry}
                type={group.type}
                onChange={(k, v) => setEntryCell(gi, ei, k, v)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      {/* ── Modern header ── */}
      <div
        className={`relative overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 mb-6`}
      >
        <div
          className={`absolute inset-0 bg-gradient-to-br ${GRAD[meta.accent]}`}
        />
        <div className="relative p-6 sm:p-8">
          <Link
            to={meta.back}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-4"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to {entity}
          </Link>
          <div className="flex items-start gap-4">
            <div
              className={`grid h-14 w-14 place-items-center rounded-2xl ${ICON_BG[meta.accent]}`}
            >
              <Icon className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Layers className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                  Bulk add
                </span>
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white mt-1">
                {schema.title}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Pick a type (e.g. Mutual Funds) and add as many entries as you
                like — then add more types below. Review everything before
                saving.
              </p>
            </div>
          </div>

          {/* Usage pill */}
          {usage && (
            <div className="mt-5">
              {usage.limit == null ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                  <Crown className="h-3.5 w-3.5" /> Unlimited · Premium
                </span>
              ) : (
                <div className="inline-flex items-center gap-3 rounded-full bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 px-4 py-1.5">
                  <span className="text-xs text-slate-500">
                    {usage.used} of {usage.limit} used
                  </span>
                  <span className="h-3 w-px bg-slate-300 dark:bg-slate-600" />
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    {maxNew} left on Free
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Cap reached ── */}
      {usage?.limit != null && maxNew === 0 ? (
        <div className="card-soft p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-amber-500/15 text-amber-600">
            <Crown className="h-7 w-7" />
          </div>
          <h3 className="mt-4 font-display text-lg font-bold text-slate-900 dark:text-white">
            You've reached the Free plan limit
          </h3>
          <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
            You already have {usage.used} of {usage.limit} {entity}. Upgrade to
            Premium to add more.
          </p>
          <Link
            to="/dashboard/settings"
            className="btn-primary mt-5 inline-flex"
          >
            <Crown className="h-4 w-4" /> Upgrade to Premium
          </Link>
        </div>
      ) : mode === "preview" ? (
        <>
          {/* ── Review banner ── */}
          <div className="mb-4 flex items-center gap-2.5 rounded-2xl border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-3">
            <ListChecks className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <p className="text-sm text-emerald-800 dark:text-emerald-200">
              Review your {flatCount} {flatCount === 1 ? meta.noun : entity}{" "}
              before adding. Use <span className="font-semibold">Edit</span> on
              any entry to fix just that one, or{" "}
              <span className="font-semibold">Edit all</span> to go back.
            </p>
          </div>

          {/* ── Preview grouped by type ── */}
          <div className="space-y-6">
            {groups.map((group, gi) => (
              <div key={group.id} className="space-y-3">
                <GroupLabel
                  meta={meta}
                  label={typeLabel(group.type)}
                  count={group.entries.length}
                />
                {group.entries.map((entry, ei) =>
                  edit && edit.gi === gi && edit.ei === ei ? (
                    <div key={ei}>
                      {editableEntry(group, gi, entry, ei, { inline: true })}
                    </div>
                  ) : (
                    <PreviewCard
                      key={ei}
                      row={entry}
                      index={ei}
                      schema={schema}
                      meta={meta}
                      onEdit={() => setEdit({ gi, ei })}
                    />
                  ),
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* ── Type groups ── */}
          <div className="space-y-5">
            {groups.map((group, gi) => (
              <div key={group.id} className="card-soft p-5 space-y-4">
                {/* Group header: the ONE type selector for this group */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                      Type
                    </span>
                    <select
                      value={group.type}
                      onChange={(e) => setGroupType(gi, e.target.value)}
                      className={`text-sm font-semibold px-3 py-1.5 rounded-xl border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 ring-1 ${RING[meta.accent]}`}
                    >
                      {schema.typeOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <span className="text-xs text-slate-400">
                      {group.entries.length}{" "}
                      {group.entries.length === 1 ? "entry" : "entries"}
                    </span>
                  </div>
                  <button
                    onClick={() => removeGroup(gi)}
                    disabled={groups.length === 1}
                    className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-30"
                    title="Remove this type"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Entries of this type */}
                <div className="space-y-3">
                  {group.entries.map((entry, ei) => (
                    <div key={ei}>{editableEntry(group, gi, entry, ei)}</div>
                  ))}
                </div>

                {/* Add another entry of the SAME type */}
                <button
                  onClick={() => addEntry(gi)}
                  disabled={atCap}
                  className="w-full rounded-xl border border-dashed border-slate-300 dark:border-slate-700 py-2.5 text-xs font-semibold text-slate-500 hover:text-emerald-600 hover:border-emerald-400 transition disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  <Plus className="h-3.5 w-3.5" /> Add another{" "}
                  {typeLabel(group.type)}
                </button>
              </div>
            ))}
          </div>

          {/* ── Add another type ── */}
          <button
            onClick={addGroup}
            disabled={atCap}
            className="mt-4 w-full rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 py-4 text-sm font-semibold text-slate-500 hover:text-emerald-600 hover:border-emerald-400 transition disabled:opacity-40 disabled:hover:text-slate-500 disabled:hover:border-slate-300 flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" /> Add another type
          </button>
          {atCap && (
            <p className="mt-2 text-center text-[11px] text-amber-600">
              You can add up to {maxNew} on the Free plan. Upgrade for
              unlimited.
            </p>
          )}
        </>
      )}

      {/* ── Sticky action bar ── */}
      {maxNew > 0 && (
        <div className="sticky bottom-0 mt-6 -mx-4 sm:-mx-6 lg:-mx-10 border-t border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-950/90 backdrop-blur px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {mode === "preview" ? (
              <span className="inline-flex items-center gap-1.5 font-medium text-emerald-600">
                <ListChecks className="h-3.5 w-3.5" /> Reviewing {flatCount}{" "}
                {flatCount === 1 ? "entry" : "entries"}
              </span>
            ) : (
              <>
                {flatCount} {flatCount === 1 ? "entry" : "entries"} ·{" "}
                {groups.length} {groups.length === 1 ? "type" : "types"}
              </>
            )}
            {progress && (
              <span className="ml-2 text-emerald-600">· saving {progress.total} at once…</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {mode === "preview" ? (
              <>
                <button
                  onClick={backToEditAll}
                  disabled={saving}
                  className="btn-secondary !text-xs"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit all
                </button>
                <button
                  onClick={submit}
                  disabled={saving || flatCount === 0 || edit !== null}
                  className="btn-primary !text-xs"
                  title={
                    edit !== null
                      ? "Finish editing the open entry first"
                      : undefined
                  }
                >
                  {saving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  )}
                  {saving
                    ? "Adding…"
                    : `Confirm & Add ${flatCount} ${flatCount === 1 ? meta.noun : entity}`}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate(meta.back)}
                  className="btn-secondary !text-xs"
                >
                  Cancel
                </button>
                <button
                  onClick={goPreview}
                  disabled={saving || flatCount === 0}
                  className="btn-primary !text-xs"
                >
                  <ListChecks className="h-3.5 w-3.5" />
                  {`Review ${flatCount} ${flatCount === 1 ? meta.noun : entity}`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
